type MessageHandler = (data: unknown) => void
type ErrorHandler = (error: Event) => void

interface WebSocketOptions {
  reconnectInterval?: number
  maxReconnectAttempts?: number
  onOpen?: () => void
  onClose?: () => void
  onError?: ErrorHandler
}

class WebSocketManager {
  private connections: Map<string, WebSocket> = new Map()
  private messageHandlers: Map<string, Set<MessageHandler>> = new Map()
  private reconnectAttempts: Map<string, number> = new Map()
  private options: Map<string, WebSocketOptions> = new Map()

  private getWsUrl(endpoint: string): string {
    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:'
    const host = window.location.host
    // Backend WS endpoints (see v2_server/api/routes/websocket.py) require a
    // JWT via ?token=<jwt>. Without it the server closes with code 4001 and
    // the client's auto-reconnect loop floods the logs with 403s.
    const token = localStorage.getItem('auth_token')
    const suffix = token ? `?token=${encodeURIComponent(token)}` : ''
    return `${protocol}//${host}/api/v2${endpoint}${suffix}`
  }

  connect(endpoint: string, options: WebSocketOptions = {}): void {
    if (this.connections.has(endpoint)) {
      return
    }

    // Don't open if we have no token — the server rejects unauthenticated
    // upgrades and the reconnect loop will flood the audit log.
    if (!localStorage.getItem('auth_token')) {
      return
    }

    const url = this.getWsUrl(endpoint)
    const ws = new WebSocket(url)

    this.options.set(endpoint, options)
    this.reconnectAttempts.set(endpoint, 0)

    ws.onopen = () => {
      console.log(`WebSocket connected: ${endpoint}`)
      this.reconnectAttempts.set(endpoint, 0)
      options.onOpen?.()
    }

    ws.onclose = (ev) => {
      console.log(`WebSocket closed: ${endpoint} (code=${ev.code})`)
      this.connections.delete(endpoint)
      options.onClose?.()
      // CloseEvent codes we should NOT loop on:
      //   4001 = our server's "missing/invalid JWT"
      //   4003 = license-denied (spoke middleware 403 mapped to a CloseEvent)
      //   1008 = policy violation (same cause)
      // Any of these means retrying immediately will just re-fail; skip
      // the auto-reconnect and let a higher layer (login, license refresh)
      // re-initiate the connection.
      if (ev.code === 4001 || ev.code === 4003 || ev.code === 1008) {
        console.warn(
          `WebSocket refused by server (code=${ev.code}); auto-reconnect suspended for ${endpoint}`,
        )
        return
      }
      this.attemptReconnect(endpoint)
    }

    ws.onerror = (error) => {
      console.error(`WebSocket error: ${endpoint}`, error)
      options.onError?.(error)
    }

    ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data)
        const handlers = this.messageHandlers.get(endpoint)
        if (handlers) {
          handlers.forEach((handler) => handler(data))
        }
      } catch (error) {
        console.error('Failed to parse WebSocket message:', error)
      }
    }

    this.connections.set(endpoint, ws)
  }

  private attemptReconnect(endpoint: string): void {
    const options = this.options.get(endpoint)
    const maxAttempts = options?.maxReconnectAttempts ?? 10
    const baseInterval = options?.reconnectInterval ?? 1500
    const attempts = this.reconnectAttempts.get(endpoint) ?? 0

    if (attempts >= maxAttempts) return

    // Exponential backoff with jitter: 1.5s → 3s → 6s → 12s → 24s → cap 30s.
    // Prevents reconnect storms hammering the server during a transient
    // outage (e.g. license re-seal in progress, deploy-time restart).
    const exp = Math.min(30_000, baseInterval * Math.pow(2, attempts))
    const jitter = Math.random() * 400
    const delay = exp + jitter

    setTimeout(() => {
      console.log(`Reconnecting WebSocket: ${endpoint} (attempt ${attempts + 1}/${maxAttempts}, delay=${Math.round(delay)}ms)`)
      this.reconnectAttempts.set(endpoint, attempts + 1)
      this.connect(endpoint, options ?? {})
    }, delay)
  }

  subscribe(endpoint: string, handler: MessageHandler): () => void {
    if (!this.messageHandlers.has(endpoint)) {
      this.messageHandlers.set(endpoint, new Set())
    }
    this.messageHandlers.get(endpoint)!.add(handler)

    // Return unsubscribe function
    return () => {
      this.messageHandlers.get(endpoint)?.delete(handler)
    }
  }

  send(endpoint: string, data: unknown): void {
    const ws = this.connections.get(endpoint)
    if (ws && ws.readyState === WebSocket.OPEN) {
      ws.send(JSON.stringify(data))
    } else {
      console.warn(`WebSocket not connected: ${endpoint}`)
    }
  }

  disconnect(endpoint: string): void {
    const ws = this.connections.get(endpoint)
    if (ws) {
      ws.close()
      this.connections.delete(endpoint)
      this.messageHandlers.delete(endpoint)
      this.options.delete(endpoint)
      this.reconnectAttempts.delete(endpoint)
    }
  }

  disconnectAll(): void {
    this.connections.forEach((_, endpoint) => {
      this.disconnect(endpoint)
    })
  }

  isConnected(endpoint: string): boolean {
    const ws = this.connections.get(endpoint)
    return ws?.readyState === WebSocket.OPEN
  }
}

export const wsManager = new WebSocketManager()

export default wsManager
